import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { addStandardDisposableListener, $, addDisposableListener, getWindow } from 'vscode/vscode/vs/base/browser/dom';
import { createCancelablePromise } from 'vscode/vscode/vs/base/common/async';
import { throttle, memoize } from 'vscode/vscode/vs/base/common/decorators';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { Disposable, MutableDisposable, combinedDisposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { removeAnsiEscapeCodes } from 'vscode/vscode/vs/base/common/strings';
import './media/stickyScroll.css.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IMenuService } from 'vscode/vscode/vs/platform/actions/common/actions.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { openContextMenu } from '../../../terminal/browser/terminalContextMenu.js';
import { TERMINAL_CONFIG_SECTION } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminal';
import { terminalStrings } from '../../../terminal/common/terminalStrings.js';
import { terminalStickyScrollHoverBackground, terminalStickyScrollBackground } from './terminalStickyScrollColorRegistry.js';

const _moduleId = "vs/workbench/contrib/terminalContrib/stickyScroll/browser/terminalStickyScrollOverlay";
let TerminalStickyScrollOverlay = class TerminalStickyScrollOverlay extends Disposable {
    constructor(_instance, _xterm, _xtermColorProvider, _commandDetection, xtermCtor, configurationService, contextKeyService, _contextMenuService, _keybindingService, menuService, _themeService) {
        super();
        this._instance = _instance;
        this._xterm = _xterm;
        this._xtermColorProvider = _xtermColorProvider;
        this._commandDetection = _commandDetection;
        this._contextMenuService = _contextMenuService;
        this._keybindingService = _keybindingService;
        this._themeService = _themeService;
        this._canvasAddon = this._register(( (new MutableDisposable())));
        this._refreshListeners = this._register(( (new MutableDisposable())));
        this._state = 0 ;
        this._isRefreshQueued = false;
        this._rawMaxLineCount = 5;
        this._contextMenu = this._register(menuService.createMenu(MenuId.TerminalStickyScrollContext, contextKeyService));
        this._register(Event.runAndSubscribe(this._xterm.raw.buffer.onBufferChange, buffer => {
            this._setState((buffer ?? this._xterm.raw.buffer.active).type === 'normal' ? 1  : 0 );
        }));
        this._register(Event.runAndSubscribe(configurationService.onDidChangeConfiguration, e => {
            if (!e || e.affectsConfiguration("terminal.integrated.stickyScroll.maxLineCount" )) {
                this._rawMaxLineCount = configurationService.getValue("terminal.integrated.stickyScroll.maxLineCount" );
            }
        }));
        this._register(this._instance.onDidChangeTarget(() => this._syncOptions()));
        xtermCtor.then(ctor => {
            if (this._store.isDisposed) {
                return;
            }
            this._stickyScrollOverlay = this._register(( (new ctor({
                rows: 1,
                cols: this._xterm.raw.cols,
                allowProposedApi: true,
                ...this._getOptions()
            }))));
            this._register(configurationService.onDidChangeConfiguration(e => {
                if (e.affectsConfiguration(TERMINAL_CONFIG_SECTION)) {
                    this._syncOptions();
                }
            }));
            this._register(this._themeService.onDidColorThemeChange(() => {
                this._syncOptions();
            }));
            this._register(this._xterm.raw.onResize(() => {
                this._syncOptions();
                this._refresh();
            }));
            this._getSerializeAddonConstructor().then(SerializeAddon => {
                this._serializeAddon = this._register(( (new SerializeAddon())));
                this._xterm.raw.loadAddon(this._serializeAddon);
                this._refresh();
            });
            this._syncGpuAccelerationState();
        });
    }
    lockHide() {
        this._element?.classList.add('lock-hide');
    }
    unlockHide() {
        this._element?.classList.remove('lock-hide');
    }
    _setState(state) {
        if (this._state === state) {
            return;
        }
        switch (state) {
            case 0 : {
                this._setVisible(false);
                this._uninstallRefreshListeners();
                break;
            }
            case 1 : {
                this._refresh();
                this._installRefreshListeners();
                break;
            }
        }
    }
    _installRefreshListeners() {
        if (!this._refreshListeners.value) {
            this._refreshListeners.value = combinedDisposable(Event.any(this._xterm.raw.onScroll, this._xterm.raw.onLineFeed,
            this._xterm.raw.onCursorMove)(() => this._refresh()), addStandardDisposableListener(this._xterm.raw.element.querySelector('.xterm-viewport'), 'scroll', () => this._refresh()));
        }
    }
    _uninstallRefreshListeners() {
        this._refreshListeners.clear();
    }
    _setVisible(isVisible) {
        if (isVisible) {
            this._ensureElement();
            this._syncGpuAccelerationState();
        }
        this._element?.classList.toggle("visible" , isVisible);
    }
    _refresh() {
        if (this._isRefreshQueued) {
            return;
        }
        this._isRefreshQueued = true;
        queueMicrotask(() => {
            this._refreshNow();
            this._isRefreshQueued = false;
        });
    }
    _refreshNow() {
        const command = this._commandDetection.getCommandForLine(this._xterm.raw.buffer.active.viewportY);
        this._currentStickyCommand = undefined;
        if (!command) {
            this._setVisible(false);
            return;
        }
        if (!('marker' in command)) {
            const partialCommand = this._commandDetection.currentCommand;
            if (partialCommand?.commandStartMarker && partialCommand.commandExecutedMarker) {
                this._updateContent(partialCommand, partialCommand.commandStartMarker);
                return;
            }
            this._setVisible(false);
            return;
        }
        const marker = command.marker;
        if (!marker || marker.line === -1) {
            this._setVisible(false);
            return;
        }
        this._updateContent(command, marker);
    }
    _updateContent(command, startMarker) {
        const xterm = this._xterm.raw;
        if (!xterm.element?.parentElement || !this._stickyScrollOverlay || !this._serializeAddon) {
            return;
        }
        if (command.promptStartMarker?.line === -1) {
            this._setVisible(false);
            return;
        }
        const buffer = xterm.buffer.active;
        const promptRowCount = command.getPromptRowCount();
        const commandRowCount = command.getCommandRowCount();
        const stickyScrollLineStart = startMarker.line - (promptRowCount - 1);
        const isPartialCommand = !('getOutput' in command);
        const rowOffset = !isPartialCommand && command.endMarker ? Math.max(buffer.viewportY - command.endMarker.line + 1, 0) : 0;
        const maxLineCount = Math.min(this._rawMaxLineCount, Math.floor(xterm.rows * 0.4 ));
        const stickyScrollLineCount = Math.min(promptRowCount + commandRowCount - 1, maxLineCount) - rowOffset;
        if (buffer.viewportY <= stickyScrollLineStart) {
            this._setVisible(false);
            return;
        }
        if (isPartialCommand && buffer.viewportY === buffer.baseY && buffer.cursorY === xterm.rows - 1) {
            const line = buffer.getLine(buffer.baseY + xterm.rows - 1);
            if ((buffer.cursorX === 1 && lineStartsWith(line, ':')) ||
                (buffer.cursorX === 5 && lineStartsWith(line, '(END)'))) {
                this._setVisible(false);
                return;
            }
        }
        const content = this._serializeAddon.serialize({
            range: {
                start: stickyScrollLineStart + rowOffset,
                end: stickyScrollLineStart + rowOffset + Math.max(stickyScrollLineCount - 1, 0)
            }
        });
        if (isPartialCommand && removeAnsiEscapeCodes(content).length === 0) {
            this._setVisible(false);
            return;
        }
        if (content && this._currentContent !== content ||
            this._stickyScrollOverlay.cols !== xterm.cols ||
            this._stickyScrollOverlay.rows !== stickyScrollLineCount) {
            this._stickyScrollOverlay.resize(this._stickyScrollOverlay.cols, stickyScrollLineCount);
            this._stickyScrollOverlay.write('\x1b[0m\x1b[H\x1b[2J');
            this._stickyScrollOverlay.write(content);
            this._currentContent = content;
        }
        if (content) {
            this._currentStickyCommand = command;
            this._setVisible(true);
            if (this._element) {
                const termBox = xterm.element.getBoundingClientRect();
                const rowHeight = termBox.height / xterm.rows;
                const overlayHeight = stickyScrollLineCount * rowHeight;
                let endMarkerOffset = 0;
                if (!isPartialCommand && command.endMarker && command.endMarker.line !== -1) {
                    if (buffer.viewportY + stickyScrollLineCount > command.endMarker.line) {
                        const diff = buffer.viewportY + stickyScrollLineCount - command.endMarker.line;
                        endMarkerOffset = diff * rowHeight;
                    }
                }
                this._element.style.bottom = `${termBox.height - overlayHeight + 1 + endMarkerOffset}px`;
            }
        }
        else {
            this._setVisible(false);
        }
    }
    _ensureElement() {
        if (
        this._element ||
            !this._stickyScrollOverlay ||
            !this._xterm?.raw.element?.parentElement) {
            return;
        }
        const overlay = this._stickyScrollOverlay;
        const hoverOverlay = $('.hover-overlay');
        this._element = $('.terminal-sticky-scroll', undefined, hoverOverlay);
        this._xterm.raw.element.parentElement.append(this._element);
        this._register(toDisposable(() => this._element?.remove()));
        let hoverTitle = ( localizeWithPath(_moduleId, 0, 'Navigate to Command'));
        const scrollToPreviousCommandKeybinding = this._keybindingService.lookupKeybinding("workbench.action.terminal.scrollToPreviousCommand" );
        if (scrollToPreviousCommandKeybinding) {
            const label = scrollToPreviousCommandKeybinding.getLabel();
            if (label) {
                hoverTitle += '\n' + ( localizeWithPath(
                    _moduleId,
                    1,
                    "{0} ({1})",
                    terminalStrings.scrollToPreviousCommand.value,
                    label
                ));
            }
        }
        const scrollToNextCommandKeybinding = this._keybindingService.lookupKeybinding("workbench.action.terminal.scrollToNextCommand" );
        if (scrollToNextCommandKeybinding) {
            const label = scrollToNextCommandKeybinding.getLabel();
            if (label) {
                hoverTitle += '\n' + ( localizeWithPath(
                    _moduleId,
                    1,
                    "{0} ({1})",
                    terminalStrings.scrollToNextCommand.value,
                    label
                ));
            }
        }
        hoverOverlay.title = hoverTitle;
        const scrollBarWidth = this._xterm.raw._core.viewport?.scrollBarWidth;
        if (scrollBarWidth !== undefined) {
            this._element.style.right = `${scrollBarWidth}px`;
        }
        this._stickyScrollOverlay.open(this._element);
        this._register(addStandardDisposableListener(hoverOverlay, 'click', () => {
            if (this._xterm && this._currentStickyCommand) {
                this._xterm.markTracker.revealCommand(this._currentStickyCommand);
                this._instance.focus();
            }
        }));
        this._register(addStandardDisposableListener(hoverOverlay, 'wheel', e => this._xterm?.raw.element?.dispatchEvent(( (new WheelEvent(e.type, e))))));
        this._register(addDisposableListener(hoverOverlay, 'mousedown', e => {
            e.stopImmediatePropagation();
            e.preventDefault();
        }));
        this._register(addDisposableListener(hoverOverlay, 'contextmenu', e => {
            e.stopImmediatePropagation();
            e.preventDefault();
            openContextMenu(getWindow(hoverOverlay), e, this._instance, this._contextMenu, this._contextMenuService);
        }));
        this._register(addStandardDisposableListener(hoverOverlay, 'mouseover', () => overlay.options.theme = this._getTheme(true)));
        this._register(addStandardDisposableListener(hoverOverlay, 'mouseleave', () => overlay.options.theme = this._getTheme(false)));
    }
    _syncOptions() {
        if (!this._stickyScrollOverlay) {
            return;
        }
        this._stickyScrollOverlay.resize(this._xterm.raw.cols, this._stickyScrollOverlay.rows);
        this._stickyScrollOverlay.options = this._getOptions();
        this._syncGpuAccelerationState();
    }
    _syncGpuAccelerationState() {
        if (!this._stickyScrollOverlay) {
            return;
        }
        const overlay = this._stickyScrollOverlay;
        if (this._xterm.isGpuAccelerated) {
            if (!this._canvasAddon.value && !this._pendingCanvasAddon) {
                this._pendingCanvasAddon = createCancelablePromise(async (token) => {
                    const CanvasAddon = await this._getCanvasAddonConstructor();
                    if (!token.isCancellationRequested && !this._store.isDisposed) {
                        this._canvasAddon.value = ( (new CanvasAddon()));
                        if (this._canvasAddon.value) {
                            overlay.loadAddon(this._canvasAddon.value);
                        }
                    }
                    this._pendingCanvasAddon = undefined;
                });
            }
        }
        else {
            this._canvasAddon.clear();
            this._pendingCanvasAddon?.cancel();
            this._pendingCanvasAddon = undefined;
        }
    }
    _getOptions() {
        const o = this._xterm.raw.options;
        return {
            allowTransparency: true,
            cursorInactiveStyle: 'none',
            scrollback: 0,
            logLevel: 'off',
            theme: this._getTheme(false),
            documentOverride: o.documentOverride,
            fontFamily: o.fontFamily,
            fontWeight: o.fontWeight,
            fontWeightBold: o.fontWeightBold,
            fontSize: o.fontSize,
            letterSpacing: o.letterSpacing,
            lineHeight: o.lineHeight,
            drawBoldTextInBrightColors: o.drawBoldTextInBrightColors,
            minimumContrastRatio: o.minimumContrastRatio,
            tabStopWidth: o.tabStopWidth,
            overviewRulerWidth: o.overviewRulerWidth,
        };
    }
    _getTheme(isHovering) {
        const theme = this._themeService.getColorTheme();
        return {
            ...this._xterm.getXtermTheme(),
            background: isHovering
                ? theme.getColor(terminalStickyScrollHoverBackground)?.toString() ?? this._xtermColorProvider.getBackgroundColor(theme)?.toString()
                : theme.getColor(terminalStickyScrollBackground)?.toString() ?? this._xtermColorProvider.getBackgroundColor(theme)?.toString(),
            selectionBackground: undefined,
            selectionInactiveBackground: undefined
        };
    }
    async _getCanvasAddonConstructor() {
        const m = await import('@xterm/addon-canvas').then(module => module.default ?? module);
        return m.CanvasAddon;
    }
    async _getSerializeAddonConstructor() {
        const m = await import('@xterm/addon-serialize').then(module => module.default ?? module);
        return m.SerializeAddon;
    }
};
TerminalStickyScrollOverlay.__decorator = ( (__decorate([
    throttle(0)
], TerminalStickyScrollOverlay.prototype, "_syncOptions", null)));
TerminalStickyScrollOverlay.__decorator = ( (__decorate([
    memoize
], TerminalStickyScrollOverlay.prototype, "_getCanvasAddonConstructor", null)));
TerminalStickyScrollOverlay.__decorator = ( (__decorate([
    memoize
], TerminalStickyScrollOverlay.prototype, "_getSerializeAddonConstructor", null)));
TerminalStickyScrollOverlay = ( (__decorate([
    ( (__param(5, IConfigurationService))),
    ( (__param(6, IContextKeyService))),
    ( (__param(7, IContextMenuService))),
    ( (__param(8, IKeybindingService))),
    ( (__param(9, IMenuService))),
    ( (__param(10, IThemeService)))
], TerminalStickyScrollOverlay)));
function lineStartsWith(line, text) {
    if (!line) {
        return false;
    }
    for (let i = 0; i < text.length; i++) {
        if (line.getCell(i)?.getChars() !== text[i]) {
            return false;
        }
    }
    return true;
}

export { TerminalStickyScrollOverlay };
